import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import PLayout from "./PLayout";
import { useApp } from "@/context/AppContext";
import { useAppUi } from "@/i18n/appUi";
import { getWorksheet, submitWorksheet } from "@/lib/api";
import { toast } from "sonner";

function Field({ field, value, onChange, t }) {
  const { type, label, options } = field;
  return (
    <div className="rbl-field" data-testid={`field-${field.id}`}>
      <label className="rbl-label">{label}</label>
      {type === "long_text" && <textarea className="rbl-input" value={value || ""} onChange={(e) => onChange(e.target.value)} />}
      {type === "short_text" && <input className="rbl-input" value={value || ""} onChange={(e) => onChange(e.target.value)} />}
      {type === "scale_1_5" && (
        <div className="rbl-scale">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" className={String(value) === String(n) ? "sel" : ""} onClick={() => onChange(String(n))}>{n}</button>
          ))}
        </div>
      )}
      {type === "yes_no" && (
        <div className="rbl-scale">
          {["yes", "no"].map((v) => (
            <button key={v} type="button" style={{ width: "auto", padding: "0 18px" }}
              className={value === v ? "sel" : ""} onClick={() => onChange(v)}>{v === "yes" ? t("yes") : t("no")}</button>
          ))}
        </div>
      )}
      {type === "multiple_choice" && (options || []).map((opt, i) => (
        <button key={i} type="button" className={`rbl-choice ${String(value) === String(i) ? "sel" : ""}`} onClick={() => onChange(String(i))}>{opt}</button>
      ))}
    </div>
  );
}

export default function WorksheetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useApp();
  const t = useAppUi(lang);
  const [w, setW] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getWorksheet(id).then((data) => {
      setW(data);
      setAnswers(data.submission?.answers || {});
    }).catch(() => setW(false));
  }, [id]);

  const save = async () => {
    setBusy(true);
    try {
      await submitWorksheet(id, answers);
      toast.success(t("toastWsSaved"));
      navigate("/app/worksheets");
    } catch {
      toast.error(t("toastWsErr"));
    } finally { setBusy(false); }
  };

  if (w === null) return <PLayout><div className="rbl-spinner" /></PLayout>;
  if (!w) return <PLayout><p>{t("wsNotFound")}</p></PLayout>;

  return (
    <PLayout>
      <Link to="/app/worksheets" className="rbl-back">{t("wsBack")}</Link>
      <h1 className="rbl-h1" style={{ fontSize: 32, marginTop: 14 }} data-testid="worksheet-title">{w.title}</h1>
      {w.description && <p className="rbl-intro">{w.description}</p>}
      <div className="rbl-worksheet" style={{ marginTop: 18 }}>
        {(w.fields || []).map((f) => (
          <Field key={f.id} field={f} value={answers[f.id]} onChange={(v) => setAnswers((a) => ({ ...a, [f.id]: v }))} t={t} />
        ))}
        <button className="rbl-btn block" onClick={save} disabled={busy} data-testid="worksheet-save">
          {busy ? t("wsSaving") : t("wsSave")}
        </button>
      </div>
    </PLayout>
  );
}
